import { useRef } from 'react';
import { UseFormReset } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { DaysParameters, getRepositoryQueryKey, SetSellLimitParameters } from '@mews-commander/data-access';
import { TextKey } from '@mews-commander/enums';
import { createZodFormResolver, lt, showSuccessAlert, useFormControls } from '@mews/framework';
import { useClearSellLimits, useSetSellLimit } from '../../../queries';
import { useSellLimitsConfirmationDialog } from '../../../hooks';
import { DIALOG_PORTAL_CONTAINER_ID } from '../constants';
import { FormValues, validationSchema } from './schema';

export type UseDailySellLimitsFormControlsParams = Pick<
    SetSellLimitParameters,
    'rateGroupIds' | 'rateIds' | 'resourceCategoryIds' | 'serviceId'
> & {
    defaultValues: FormValues;
    onRemove?: () => void;
    timeUnit: string;
};

export const useDailySellLimitsFormControls = ({
    defaultValues,
    onRemove,
    timeUnit,
    ...params
}: UseDailySellLimitsFormControlsParams) => {
    const setLimitsQuery = useSetSellLimit();
    const clearLimitsQuery = useClearSellLimits();
    const queryClient = useQueryClient();
    const resetFunctionRef = useRef<UseFormReset<FormValues> | undefined>();

    const commonParams = {
        firstTimeUnitStartUtc: timeUnit,
        lastTimeUnitStartUtc: timeUnit,
        days: allDays,
    };

    const onSubmit = (values: FormValues) => {
        setLimitsQuery.mutate(
            {
                ...params,
                ...values,
                ...commonParams,
            },
            {
                onSuccess: async () => {
                    showSuccessAlert({
                        title: lt('SellLimitsAppliedSuccessMessage'),
                    });
                    resetFunctionRef.current?.(defaultValues, { keepValues: true });
                    await queryClient.invalidateQueries({ queryKey: getRepositoryQueryKey('sellLimits', 'get') });
                },
            }
        );
    };

    const formControls = useFormControls<FormValues>({
        onSubmit,
        defaultValues,
        values: defaultValues,
        resolver,
    });

    resetFunctionRef.current = formControls.reset;

    const handleRemoveLimits = (e: React.MouseEvent) => {
        e.nativeEvent.stopImmediatePropagation();
        clearLimitsQuery.mutate(
            {
                ...params,
                ...commonParams,
            },
            {
                onSuccess: async () => {
                    showSuccessAlert({
                        title: lt('SellLimitRemoved'),
                        description: lt('DailySellLimitRemovedMessage'),
                    });
                    onRemove?.();
                    await queryClient.invalidateQueries({ queryKey: getRepositoryQueryKey('sellLimits', 'get') });
                },
            }
        );
    };

    const handleClose = (e: React.MouseEvent) => {
        e.nativeEvent.stopImmediatePropagation();
    };

    const removeLimits = useSellLimitsConfirmationDialog({
        onConfirm: handleRemoveLimits,
        primaryActionKey: TextKey.Remove,
        dialogTitleKey: TextKey.RemoveSellLimitTitle,
        dialogBodyKey: TextKey.RemoveDailyLimitConfirmationText,
        portalContainerId: DIALOG_PORTAL_CONTAINER_ID,
        onClose: handleClose,
    });

    return {
        ...formControls,
        isSubmitting: setLimitsQuery.isPending || clearLimitsQuery.isPending,
        removeLimits,
    };
};

const resolver = createZodFormResolver(validationSchema);
const allDays = {
    monday: true,
    tuesday: true,
    wednesday: true,
    thursday: true,
    friday: true,
    saturday: true,
    sunday: true,
} satisfies DaysParameters;
